import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CreditCard, Building2, User, Mail, Phone, MapPin, FileText, ShoppingCart, ArrowRight } from 'lucide-react';
import AlertModal from './AlertModal';

const PaymentModal = ({ isOpen, onClose, plan, isYearly }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'tr';
  const [step, setStep] = useState(1); // 1: Form, 2: Payment
  const [formData, setFormData] = useState({
    companyName: '',
    companyTaxNumber: '',
    address: '',
    taxOffice: '',
    authorizedPersonName: '',
    authorizedPersonSurname: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'error', title: '', message: '' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal açıldığında state'leri sıfırla
  useEffect(() => {
    if (isOpen) {
      setFormData({
        companyName: '',
        companyTaxNumber: '',
        address: '',
        taxOffice: '',
        authorizedPersonName: '',
        authorizedPersonSurname: '',
        email: '',
        phone: ''
      });
      setCardData({
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: ''
      });
      setErrors({});
      setStep(1);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Dolar kuru (not: 42 TL)
  const USD_TO_TRY = 42;
  const KDV_RATE = 0.20; // %20 KDV

  // Fiyat hesaplama
  const getPrice = () => {
    if (!plan || typeof plan.monthlyPrice !== 'number') return 0;
    const basePrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
    return isYearly ? basePrice * 12 : basePrice;
  };

  const basePriceUSD = getPrice();
  const basePriceTRY = basePriceUSD * USD_TO_TRY;
  const kdvAmount = basePriceTRY * KDV_RATE;
  const totalPriceTRY = basePriceTRY + kdvAmount;

  // Dile göre fiyat formatlama fonksiyonu
  const formatPrice = (price) => {
    if (currentLanguage === 'en') {
      // İngilizce format: binlik ayırıcı virgül, ondalık nokta (1,234.56)
      return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      // Türkçe format: binlik ayırıcı nokta, ondalık virgül (1.234,56)
      return price.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Hata temizle
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Kart numarası formatla (4'er haneli gruplar)
    if (name === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) formattedValue = formattedValue.slice(0, 19);
    }
    // Son kullanma tarihi formatla (MM/YY)
    if (name === 'expiryDate') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2, 4);
      }
      if (formattedValue.length > 5) formattedValue = formattedValue.slice(0, 5);
    }
    // CVV sadece 3-4 rakam
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setCardData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = `${t('paymentModal.companyName')} ${t('paymentModal.required')}`;
    if (!formData.companyTaxNumber.trim()) newErrors.companyTaxNumber = `${t('paymentModal.taxNumber')} ${t('paymentModal.required')}`;
    if (!formData.address.trim()) newErrors.address = `${t('paymentModal.address')} ${t('paymentModal.required')}`;
    if (!formData.taxOffice.trim()) newErrors.taxOffice = `${t('paymentModal.taxOffice')} ${t('paymentModal.required')}`;
    if (!formData.authorizedPersonName.trim()) newErrors.authorizedPersonName = `${t('paymentModal.authorizedPersonName')} ${t('paymentModal.required')}`;
    if (!formData.authorizedPersonSurname.trim()) newErrors.authorizedPersonSurname = `${t('paymentModal.authorizedPersonSurname')} ${t('paymentModal.required')}`;
    if (!formData.email.trim()) {
      newErrors.email = `${t('paymentModal.email')} ${t('paymentModal.required')}`;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('paymentModal.invalidEmail');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = `${t('paymentModal.phone')} ${t('paymentModal.required')}`;
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('paymentModal.invalidPhone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCard = () => {
    const newErrors = {};
    if (!cardData.cardNumber.replace(/\s/g, '')) newErrors.cardNumber = `${t('paymentModal.cardNumber')} ${t('paymentModal.required')}`;
    if (!cardData.cardName.trim()) newErrors.cardName = `${t('paymentModal.cardName')} ${t('paymentModal.required')}`;
    if (!cardData.expiryDate) newErrors.expiryDate = `${t('paymentModal.expiryDate')} ${t('paymentModal.required')}`;
    if (!cardData.cvv) newErrors.cvv = `${t('paymentModal.cvv')} ${t('paymentModal.required')}`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = () => {
    if (validateForm()) {
      setStep(2);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!validateCard() || isProcessing) return;

    setIsProcessing(true);

    try {
      // Tüm firma bilgilerini fullName alanında birleştir
      const taxNoLabel = currentLanguage === 'en' ? 'Tax No' : 'Vergi No';
      const addressLabel = currentLanguage === 'en' ? 'Address' : 'Adres';
      const taxOfficeLabel = currentLanguage === 'en' ? 'Tax Office' : 'Vergi Dairesi';
      const authorizedLabel = currentLanguage === 'en' ? 'Authorized' : 'Yetkili';
      const fullName = `${formData.companyName} | ${taxNoLabel}: ${formData.companyTaxNumber} | ${addressLabel}: ${formData.address} | ${taxOfficeLabel}: ${formData.taxOffice} | ${authorizedLabel}: ${formData.authorizedPersonName} ${formData.authorizedPersonSurname}`;

      // Contact API'sine gönder
      const response = await fetch('https://report.hirethink.ai/api/Email/SendContactForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: fullName,
          email: formData.email,
          phone: formData.phone,
          employeeCount: ''
        })
      });

      if (response.ok) {
        // Hata mesajı göster (ödeme başarısız)
        setAlertModal({
          isOpen: true,
          type: 'error',
          title: t('paymentModal.paymentFailed'),
          message: t('paymentModal.paymentFailedMessage')
        });
      } else {
        throw new Error('Form gönderilemedi');
      }
    } catch (error) {
      // Hata durumunda da mesaj göster
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: t('paymentModal.paymentFailed'),
        message: t('paymentModal.paymentFailedMessage')
      });
      console.error('Ödeme hatası:', error);
    }
  };

  const handleAlertClose = () => {
    setAlertModal({ ...alertModal, isOpen: false });
    // İşlem durumunu sıfırla
    setIsProcessing(false);
    // Modal'ı kapat
    onClose();
    // Form verilerini temizle
    setFormData({
      companyName: '',
      companyTaxNumber: '',
      address: '',
      taxOffice: '',
      authorizedPersonName: '',
      authorizedPersonSurname: '',
      email: '',
      phone: ''
    });
    setCardData({
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: ''
    });
    setStep(1);
  };

  if (!isOpen || !plan) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="bg-primary p-4 sm:p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-lg sm:text-xl md:text-2xl font-heading truncate">
                {step === 1 ? t('paymentModal.invoiceInfo') : t('paymentModal.payment')}
              </h2>
              <p className="text-white/80 text-xs sm:text-sm mt-1 truncate">
                {plan.name} - {isYearly ? t('paymentModal.yearly') : t('paymentModal.monthly')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors duration-200 p-2 flex-shrink-0 touch-manipulation"
              aria-label={t('paymentModal.close')}
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {step === 1 ? (
            <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Form */}
              <div className="lg:col-span-2 order-2 lg:order-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                  <span className="truncate">{t('paymentModal.companyInfo')}</span>
                </h3>
                <form className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('paymentModal.companyName')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName || ''}
                      onChange={handleFormChange}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                        errors.companyName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('paymentModal.companyNamePlaceholder')}
                    />
                    {errors.companyName && (
                      <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.taxNumber')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="text"
                        name="companyTaxNumber"
                        value={formData.companyTaxNumber || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.companyTaxNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.taxNumberPlaceholder')}
                      />
                      {errors.companyTaxNumber && (
                        <p className="text-xs text-red-500 mt-1">{errors.companyTaxNumber}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.taxOffice')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="text"
                        name="taxOffice"
                        value={formData.taxOffice || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.taxOffice ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.taxOfficePlaceholder')}
                      />
                      {errors.taxOffice && (
                        <p className="text-xs text-red-500 mt-1">{errors.taxOffice}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('paymentModal.address')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                    </label>
                    <textarea
                      name="address"
                      value={formData.address || ''}
                      onChange={handleFormChange}
                      rows={3}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation resize-none ${
                        errors.address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('paymentModal.addressPlaceholder')}
                    />
                    {errors.address && (
                      <p className="text-xs text-red-500 mt-1">{errors.address}</p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.authorizedPersonName')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="text"
                        name="authorizedPersonName"
                        value={formData.authorizedPersonName || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.authorizedPersonName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.authorizedPersonNamePlaceholder')}
                      />
                      {errors.authorizedPersonName && (
                        <p className="text-xs text-red-500 mt-1">{errors.authorizedPersonName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.authorizedPersonSurname')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="text"
                        name="authorizedPersonSurname"
                        value={formData.authorizedPersonSurname || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.authorizedPersonSurname ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.authorizedPersonSurnamePlaceholder')}
                      />
                      {errors.authorizedPersonSurname && (
                        <p className="text-xs text-red-500 mt-1">{errors.authorizedPersonSurname}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.email')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.emailPlaceholder')}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {t('paymentModal.phone')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleFormChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('paymentModal.phonePlaceholder')}
                      />
                      {errors.phone && (
                        <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              {/* Sepet Özeti */}
              <div className="lg:col-span-1 order-1 lg:order-2">
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 lg:sticky lg:top-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                    <span>{t('paymentModal.orderSummary')}</span>
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t('paymentModal.plan')}</span>
                      <span className="font-medium text-right truncate ml-2">{plan.name}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t('paymentModal.period')}</span>
                      <span className="font-medium">{isYearly ? `12 ${t('paymentModal.months')}` : `1 ${t('paymentModal.month')}`}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t('paymentModal.unitPrice')}</span>
                      <span className="font-medium">${isYearly ? plan.yearlyPrice : plan.monthlyPrice}</span>
                    </div>
                    {isYearly && (
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600">{t('paymentModal.total12Months')}</span>
                        <span className="font-medium">${basePriceUSD}</span>
                      </div>
                    )}
                     <div className="border-t border-gray-200 pt-2 sm:pt-3 mt-2 sm:mt-3">
                       <div className="flex justify-between text-xs sm:text-sm mb-1.5 sm:mb-2">
                         <span className="text-gray-600">{t('paymentModal.subtotal')}</span>
                         <span className="font-medium">{formatPrice(basePriceTRY)} ₺</span>
                       </div>
                       <div className="flex justify-between text-xs sm:text-sm mb-1.5 sm:mb-2">
                         <span className="text-gray-600">{t('paymentModal.vat')}</span>
                         <span className="font-medium">{formatPrice(kdvAmount)} ₺</span>
                       </div>
                       <div className="flex justify-between text-base sm:text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                         <span>{t('paymentModal.total')}</span>
                         <span>{formatPrice(totalPriceTRY)} ₺</span>
                       </div>
                     </div>
                    <div className="text-xs text-gray-500 mt-2 leading-tight">
                      {t('paymentModal.exchangeRateNote', { rate: USD_TO_TRY })}
                    </div>
                  </div>
                  <button
                    onClick={handleProceedToPayment}
                    className="w-full mt-4 sm:mt-6 bg-primary hover:bg-primary-dark text-white py-3 sm:py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                  >
                    {t('paymentModal.proceedToPayment')}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                <span>{t('paymentModal.cardInfo')}</span>
              </h3>
              <form onSubmit={handlePayment} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t('paymentModal.cardNumber')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                  </label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={cardData.cardNumber || ''}
                    onChange={handleCardChange}
                    placeholder={t('paymentModal.cardNumberPlaceholder')}
                    maxLength={19}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                      errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.cardNumber && (
                    <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t('paymentModal.cardName')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                  </label>
                  <input
                    type="text"
                    name="cardName"
                    value={cardData.cardName || ''}
                    onChange={handleCardChange}
                    placeholder={t('paymentModal.cardNamePlaceholder')}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all uppercase touch-manipulation ${
                      errors.cardName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.cardName && (
                    <p className="text-xs text-red-500 mt-1">{errors.cardName}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('paymentModal.expiryDate')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                    </label>
                    <input
                      type="text"
                      name="expiryDate"
                      value={cardData.expiryDate || ''}
                      onChange={handleCardChange}
                      placeholder={t('paymentModal.expiryDatePlaceholder')}
                      maxLength={5}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                        errors.expiryDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.expiryDate && (
                      <p className="text-xs text-red-500 mt-1">{errors.expiryDate}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('paymentModal.cvv')} <span className="text-red-500">{t('paymentModal.requiredField')}</span>
                    </label>
                    <input
                      type="text"
                      name="cvv"
                      value={cardData.cvv || ''}
                      onChange={handleCardChange}
                      placeholder={t('paymentModal.cvvPlaceholder')}
                      maxLength={4}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all touch-manipulation ${
                        errors.cvv ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.cvv && (
                      <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>
                    )}
                  </div>
                </div>

                 {/* Sepet Özeti (Ödeme sayfasında da göster) */}
                 <div className="bg-gray-50 rounded-xl p-4 sm:p-4 mt-4 sm:mt-6">
                   <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-2 sm:mb-3">{t('paymentModal.paymentSummary')}</h4>
                   <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                     <div className="flex justify-between">
                       <span className="text-gray-600">{t('paymentModal.subtotal')}</span>
                       <span className="font-medium">{formatPrice(basePriceTRY)} ₺</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600">{t('paymentModal.vat')}</span>
                       <span className="font-medium">{formatPrice(kdvAmount)} ₺</span>
                     </div>
                     <div className="flex justify-between text-base sm:text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                       <span>{t('paymentModal.total')}</span>
                       <span>{formatPrice(totalPriceTRY)} ₺</span>
                     </div>
                   </div>
                 </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 touch-manipulation text-sm sm:text-base"
                  >
                    {t('paymentModal.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className={`flex-1 bg-accent-orange hover:bg-accent-orange-hover text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base ${
                      isProcessing ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('paymentModal.processing')}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        {t('paymentModal.pay')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={handleAlertClose}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default PaymentModal;

